import { type ChangeEvent, useEffect } from 'react';
import { Inline, Radio, RadioGroup } from '@etg/wings';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { TripType, type TripType as TripTypeValue } from '@eti/schema-types';
import { useController, useFormContext } from 'react-hook-form';
import { screenReadersOnlyStyles } from '../../../utils/styleUtils';
import type { FormBound } from '../../../common/search-form-panel/SearchFormNewContainer';
import {
  DEPARTURE_DATE,
  DEPARTURE_TIME_OF_DAY,
  DESTINATION,
  DIRECT_FLIGHT,
  MULTI_BOUND,
  ORIGIN,
  RETURN_DATE,
  RETURN_TIME_OF_DAY,
  SINGLE_BOUND,
} from '../constants/formFields';

const openJawTripTypes: TripTypeValue[] = [
  TripType.OpenJawSingleDestination,
  TripType.OpenJawSingleOrigin,
  TripType.OpenJawDouble,
];

const labelPositionStyles = css`
  margin-top: 0.125rem;
`;

const TripTypesNew = ({ fieldName }: { fieldName: string }) => {
  const { currentSearchData, setSearchData } = useSearch();
  const { t } = useTranslation();
  const { p } = useProperty();
  const { control, setValue, watch, trigger } = useFormContext();
  const {
    field: { onChange, name, value },
  } = useController({ control, name: fieldName, rules: { required: true } });
  const [singleBound, multiBound] = watch(['singleBound', 'multiBound']);

  const isEmptyBound = (bound: FormBound) =>
    !bound || Object.keys(bound).length === 0 || (!bound[ORIGIN] && !bound[DESTINATION]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;

    if (selectedValue === TripType.OneWay) {
      setValue(`${SINGLE_BOUND}.${RETURN_DATE}`, '');
      setValue(`${SINGLE_BOUND}.${RETURN_TIME_OF_DAY}`, '');
    }

    if (selectedValue === TripType.MultiStop) {
      setValue(`${SINGLE_BOUND}.${RETURN_DATE}`, '');
      setValue(`${SINGLE_BOUND}.${RETURN_TIME_OF_DAY}`, '');
      setValue(DIRECT_FLIGHT, false);
    }
    const isMultiBoundEmpty =
      multiBound && multiBound.every((bound: FormBound) => isEmptyBound(bound));

    if (selectedValue === TripType.MultiStop && isMultiBoundEmpty && !isEmptyBound(singleBound)) {
      [ORIGIN, DESTINATION, DEPARTURE_DATE, DEPARTURE_TIME_OF_DAY].forEach((key) => {
        if (singleBound[key]) {
          setValue(`${MULTI_BOUND}[0].${key}`, singleBound[key], { shouldTouch: true });
        }
      });
    }
    if (selectedValue !== TripType.MultiStop && isEmptyBound(singleBound)) {
      if (multiBound[0]?.origin?.name) {
        [ORIGIN, DESTINATION, DEPARTURE_DATE, DEPARTURE_TIME_OF_DAY].forEach((key) => {
          if (multiBound[0][key]) {
            setValue(`${SINGLE_BOUND}.${key}`, multiBound[0][key], { shouldTouch: true });
          }
        });
      }
    }
    onChange(selectedValue);
    if (selectedValue === TripType.Return) {
      trigger(`${SINGLE_BOUND}.${RETURN_DATE}`);
    }
  };

  useEffect(() => {
    if (
      currentSearchData?.tripType &&
      openJawTripTypes.includes(currentSearchData.tripType) &&
      setSearchData
    ) {
      setSearchData({
        ...currentSearchData,
        tripType: TripType.MultiStop,
      });
    }
  }, [currentSearchData, setSearchData]);

  return (
    <RadioGroup
      aria-required
      name={name}
      onChange={(event) => handleChange(event)}
      selectedValue={value}
    >
      <RadioGroup.Legend className={screenReadersOnlyStyles}>
        {t('ScreenReader.SearchForm.TripTypeLegend')}
      </RadioGroup.Legend>
      <Inline align="start" noWrap>
        <Radio.Label data-testid="searchForm-return-radio-label" htmlFor="returnOption">
          <Inline noWrap spacing={8}>
            <Radio
              data-testid="searchForm-return-radio"
              id="returnOption"
              required
              value={TripType.Return}
            />
            <span className={labelPositionStyles}>{t('Air.Search.Return')}</span>
          </Inline>
        </Radio.Label>
        <Radio.Label data-testid="searchForm-oneWay-radio-label" htmlFor="oneWayOption">
          <Inline noWrap spacing={8}>
            <Radio
              data-testid="searchForm-oneWay-radio"
              id="oneWayOption"
              required
              value={TripType.OneWay}
            />
            <span className={labelPositionStyles}>{t('Air.Search.Oneway')}</span>
          </Inline>
        </Radio.Label>
        {p('IbeClient.MultiStop.Enabled') && (
          <Radio.Label data-testid="searchForm-multiStop-radio-label" htmlFor="multiStopOption">
            <Inline noWrap spacing={8}>
              <Radio
                data-testid="searchForm-multiStop-radio"
                id="multiStopOption"
                required
                value={TripType.MultiStop}
              />
              <span className={labelPositionStyles}>{t('Air.Search.MultiBound')}</span>
            </Inline>
          </Radio.Label>
        )}
      </Inline>
    </RadioGroup>
  );
};

export default TripTypesNew;
